import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { waitlistAPI } from '../services/api';
import { ArrowLeft, DownloadSimple, EnvelopeSimple, CheckCircle, Clock, UserPlus } from '@phosphor-icons/react';

const CATEGORY_LABELS = { creator: 'Creator/Artist', business: 'Small Business', agency: 'Agency/Manager', other: 'Other' };
const STATUS_META = {
  waiting: { label: 'Waiting', color: '#999999', icon: Clock },
  invited: { label: 'Invited', color: '#3B82F6', icon: EnvelopeSimple },
  onboarded: { label: 'Onboarded', color: '#22C55E', icon: CheckCircle },
};

export default function AdminWaitlistPage() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [utmSources, setUtmSources] = useState([]);
  const [filters, setFilters] = useState({ status: 'all', category: 'all', utm_source: 'all' });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({ email: '', category: 'business', social_handle: '' });
  const [addingLead, setAddingLead] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.utm_source !== 'all') params.utm_source = filters.utm_source;
      const { data } = await waitlistAPI.adminList(params);
      setEntries(data.entries || []);
      setStats(data.stats || {});
      setUtmSources(data.utm_sources || []);
    } catch (err) {
      console.error('Failed to load waitlist:', err);
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (entryId, status) => {
    setSavingId(entryId);
    try {
      const { data } = await waitlistAPI.adminUpdate(entryId, status);
      setEntries((prev) => prev.map((e) => (e.entry_id === entryId ? data : e)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      console.error('Waitlist status update failed:', err);
      toast.error('Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const addLead = async (e) => {
    e.preventDefault();
    if (!newLead.email.trim()) { toast.error('Email is required'); return; }
    setAddingLead(true);
    try {
      await waitlistAPI.adminAddLead({
        email: newLead.email.trim(),
        category: newLead.category,
        social_handle: newLead.social_handle || null,
      });
      toast.success('Wave 1 lead added (marked invited)');
      setNewLead({ email: '', category: 'business', social_handle: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Failed to add lead');
    } finally {
      setAddingLead(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.utm_source !== 'all') params.utm_source = filters.utm_source;
      const res = await waitlistAPI.adminExport(params);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'divispend-waitlist.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Waitlist CSV export failed:', err);
      toast.error('Export failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white" data-testid="admin-waitlist-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/dashboard" className="text-[#999999] hover:text-[#22C55E] flex items-center gap-2 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-2">Launch Pipeline</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Waitlist</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddLead((v) => !v)} variant="outline" className="border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-sm" data-testid="add-lead-toggle">
              <UserPlus className="w-4 h-4 mr-2" /> Add Wave 1 Lead
            </Button>
            <Button onClick={exportCsv} className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="export-csv-button">
              <DownloadSimple className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {showAddLead && (
          <form onSubmit={addLead} className="bg-[#0d0d0d] border border-[#3B82F6]/30 rounded-xl p-4 mb-8 flex flex-col sm:flex-row gap-3" data-testid="add-lead-form">
            <input
              type="email" required value={newLead.email}
              onChange={(e) => setNewLead((l) => ({ ...l, email: e.target.value }))}
              placeholder="lead@company.com"
              className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-sm px-3 h-10 text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#3B82F6]"
              data-testid="add-lead-email"
            />
            <Select value={newLead.category} onValueChange={(v) => setNewLead((l) => ({ ...l, category: v }))}>
              <SelectTrigger className="w-full sm:w-44 bg-[#1a1a1a] border-[#333] text-xs h-10" data-testid="add-lead-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#333]">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              type="text" value={newLead.social_handle}
              onChange={(e) => setNewLead((l) => ({ ...l, social_handle: e.target.value }))}
              placeholder="Social handle (optional)"
              className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-sm px-3 h-10 text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#3B82F6]"
              data-testid="add-lead-social"
            />
            <Button type="submit" disabled={addingLead} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-sm h-10" data-testid="add-lead-submit">
              {addingLead ? 'Adding…' : 'Add as Invited'}
            </Button>
          </form>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[['Total', stats.total], ['Waiting', stats.waiting], ['Invited', stats.invited], ['Onboarded', stats.onboarded]].map(([label, val]) => (
            <div key={label} className="bg-[#1a1a1a] border border-[#222] rounded-xl p-4" data-testid={`stat-${label.toLowerCase()}`}>
              <p className="text-2xl font-bold text-white">{val ?? 0}</p>
              <p className="text-xs text-[#999] uppercase tracking-wider">{label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-40 bg-[#0a0a0a] border-[#222] text-xs h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#333]">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="waiting">Waiting</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="onboarded">Onboarded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
            <SelectTrigger className="w-44 bg-[#0a0a0a] border-[#222] text-xs h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#333]">
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.utm_source} onValueChange={(v) => setFilters((f) => ({ ...f, utm_source: v }))}>
            <SelectTrigger className="w-44 bg-[#0a0a0a] border-[#222] text-xs h-9" data-testid="filter-utm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#333]">
              <SelectItem value="all">All sources</SelectItem>
              {utmSources.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-[#999]">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-[#666] border border-[#222] rounded-xl p-8 text-center">No signups match these filters yet.</p>
        ) : (
          <div className="border border-[#222] rounded-xl overflow-hidden divide-y divide-[#1a1a1a]">
            {entries.map((e) => {
              const sm = STATUS_META[e.status] || STATUS_META.waiting;
              return (
                <div key={e.entry_id} className="px-4 sm:px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 hover:bg-[#0d0d0d] transition-colors" data-testid={`waitlist-row-${e.entry_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold" style={{ background: `${sm.color}20`, color: sm.color }}>{sm.label}</span>
                      <span className="font-semibold text-white text-sm">{e.email}</span>
                    </div>
                    <p className="text-xs text-[#777]">
                      {CATEGORY_LABELS[e.category] || e.category}
                      {e.social_handle && <> · {e.social_handle}</>}
                      {e.utm_source && <> · via <span className="text-[#22C55E]">{e.utm_source}</span></>}
                      {' · '}{new Date(e.signup_date).toLocaleDateString()}
                      {' · code '}<span className="text-[#999]">{e.invite_code}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {e.status === 'waiting' && (
                      <Button size="sm" disabled={savingId === e.entry_id} onClick={() => updateStatus(e.entry_id, 'invited')}
                        className="bg-[#3B82F6]/20 hover:bg-[#3B82F6]/30 text-[#3B82F6] border border-[#3B82F6]/30 rounded-sm text-xs h-8"
                        data-testid={`invite-${e.entry_id}`}>
                        Mark Invited
                      </Button>
                    )}
                    {e.status === 'invited' && (
                      <>
                        <Button size="sm" disabled={savingId === e.entry_id} onClick={() => updateStatus(e.entry_id, 'onboarded')}
                          className="bg-[#22C55E]/20 hover:bg-[#22C55E]/30 text-[#22C55E] border border-[#22C55E]/30 rounded-sm text-xs h-8"
                          data-testid={`onboard-${e.entry_id}`}>
                          Mark Onboarded
                        </Button>
                        <Button size="sm" variant="ghost" disabled={savingId === e.entry_id} onClick={() => updateStatus(e.entry_id, 'waiting')}
                          className="text-[#777] hover:text-white text-xs h-8 rounded-sm" data-testid={`revert-${e.entry_id}`}>
                          Revert
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
